import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box, Card, CardContent, Stack, Typography, Button, Alert, Checkbox,
  FormControlLabel, FormControl, InputLabel, Select, MenuItem, Skeleton,
  CircularProgress, IconButton, Chip, LinearProgress,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import FaceCaptureFlow from '@/components/faceAnalysis/FaceCaptureFlow';
import { faceCaptureApi } from '@/services/api';
import { selectCurrentClinic, setCurrentClinic } from '@/store/clinicSlice';
import { selectIsRtl } from '@/store/uiSlice';
import { localized } from '@/utils/localizedContent';

/**
 * Client-side face capture — a copy of the clinic app's capture section, minus
 * the staff-only steps (no client lookup: the client IS the subject).
 *
 * Phases: consent -> [agent] -> capture -> review -> success
 * The clinic's policy decides whether this page is available at all, whether the
 * client picks the agent, how long the capture stays usable, and how many
 * attempts they get.
 */
export default function FaceCapturePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const isRtl = useSelector(selectIsRtl);
  const clinic = useSelector(selectCurrentClinic);
  const Back = isRtl ? ArrowForwardIcon : ArrowBackIcon;

  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get('invite') || null;
  // ROUTE 2 — the client's own consultation. There is no link to validate and
  // no clinic to belong to, so nothing here goes near capture_invites.
  const consultationId = searchParams.get('consultation') || null;
  const isConsultation = Boolean(consultationId);


  const [phase, setPhase] = useState('consent');
  const [consent, setConsent] = useState(false);
  const [agentKey, setAgentKey] = useState('');
  const [captures, setCaptures] = useState([]);
  const [error, setError] = useState('');
  const [uploadDone, setUploadDone] = useState(0);

  // A clinic-sent link: it fixes the clinic AND the AI agent. When present it
  // overrides the "current clinic" and hides the agent-choice step.
  const { data: invite, isLoading: inviteLoading, isError: inviteError } = useQuery({
    queryKey: ['capture-invite', inviteId],
    queryFn: () => faceCaptureApi.getInvite(inviteId),
    enabled: Boolean(inviteId) && !isConsultation,
    retry: false,
  });

  // A consultation capture belongs to no clinic. Clinics are simply not part
  // of this route, so the clinic gate and the clinic capture-policy must not
  // apply to it.
  const clinicCode = invite?.clinicCode || (isConsultation ? null : clinic?.code);
  const clinicName = invite?.clinicName || clinic?.name;

  // We've arrived (authenticated) at the capture page, so the login round-trip
  // is done — clear the stash so it can't hijack a later, unrelated login.
  useEffect(() => {
    if (inviteId) {
      try { sessionStorage.removeItem('pendingCaptureInvite'); } catch { /* ignore */ }
    }
  }, [inviteId]);

  // An invite means we entered THIS clinic on the client's behalf — reflect it
  // in app state (banner, dashboard, and so the record is stored correctly).
  const dispatch = useDispatch();
  useEffect(() => {
    if (invite?.clinicCode && invite.clinicCode !== clinic?.code) {
      dispatch(setCurrentClinic({
        code: invite.clinicCode,
        name: invite.clinicName,
        capturePolicy: 'client_capture',
      }));
    }
  }, [invite, clinic?.code, dispatch]);

  const { data: clinicPolicy, isLoading: policyLoading } = useQuery({
    queryKey: ['capture-policy', clinicCode],
    queryFn: () => faceCaptureApi.getPolicy(clinicCode),
    enabled: Boolean(clinicCode),
  });

  // A consultation capture is authorised by the consultation itself, not by a
  // clinic's policy — there is no clinic to ask. Without this the page would
  // fall through to "not allowed", because the policy query never runs.
  const policy = isConsultation
    ? { allowed: true, retriesLeft: 3, agentChoice: 'fixed', ...(clinicPolicy || {}) }
    : clinicPolicy;
  const isLoading = isConsultation ? false : policyLoading;

  /** Upload ONE pose with retry — small requests survive weak connections. */
  async function uploadPoseWithRetry(analysisId, pose, attempts = 3) {
    let lastErr;
    for (let a = 0; a < attempts; a += 1) {
      try {
        return await faceCaptureApi.uploadPose(analysisId, pose);
      } catch (e) {
        lastErr = e;
        const s = e?.response?.status;
        const retryable = !e?.response || e?.code === 'ECONNABORTED' || s >= 500 || s === 408 || s === 429;
        if (!retryable) throw e;
        await new Promise((r) => { setTimeout(r, 600 * (a + 1)); });
      }
    }
    throw lastErr;
  }

  const submitMutation = useMutation({
    mutationFn: async () => {
      setUploadDone(0);
      const started = await faceCaptureApi.startCapture({
        // ROUTE 2 keys off the consultation; ROUTE 1 keys off the clinic invite.
        ...(isConsultation
          ? { consultationId }
          : {
              clinicCode,
              ...(inviteId ? { inviteId } : {}),
              // send a client-picked agent when the policy lets the client choose.
              ...(!inviteId && policy?.agentChoice === 'client' && agentKey ? { agentKey } : {}),
            }),
        capturedAt: new Date().toISOString(),
        device: { ua: navigator.userAgent },
      });
      for (let i = 0; i < captures.length; i += 1) {
        await uploadPoseWithRetry(started.analysisId, captures[i]);
        setUploadDone(i + 1);
      }
      return faceCaptureApi.completeCapture(started.analysisId);
    },
    retry: false,
    onSuccess: () => {
      // A consultation capture has somewhere to be: the free preview is the
      // payoff, and a "sent successfully" screen in between is a dead stop.
      if (isConsultation && consultationId) {
        navigate(`/consultations/${encodeURIComponent(consultationId)}/preview`,
                 { replace: true });
        return;
      }
      setPhase('success');
    },
    onError: (err) => {
      const code = err?.response?.data?.error?.code;
      if (code === 'RETRIES_EXHAUSTED') setError(t('faceCapture.retriesExhausted'));
      else if (code === 'CAPTURE_NOT_ALLOWED') setError(t('faceCapture.notAllowed'));
      else if (!err?.response) setError(t('face.submitError.network'));
      else setError(t('face.error'));
    },
  });

  if (inviteId && !isConsultation && inviteError) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error" action={<Button onClick={() => navigate('/dashboard')}>{t('faceCapture.backToDashboard')}</Button>}>
          {t('faceCapture.inviteInvalid')}
        </Alert>
      </Container>
    );
  }
  if (inviteId && !isConsultation && inviteLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Box sx={{ display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
      </Container>
    );
  }
  if (!clinicCode && !isConsultation) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="info" action={<Button onClick={() => navigate('/clinic-entry')}>{t('faceCapture.enterClinic')}</Button>}>
          {t('faceCapture.noClinic')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
          <IconButton edge="start" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            <Back />
          </IconButton>
          <Typography variant="h6" fontWeight={800}>{t('faceCapture.title')}</Typography>
        </Stack>
        {isLoading ? <Skeleton variant="rounded" height={220} /> : !policy?.allowed ? (
          <Alert severity="warning">{t('faceCapture.notAllowed')}</Alert>
        ) : policy.retriesLeft <= 0 && phase !== 'success' ? (
          <Alert severity="warning">{t('faceCapture.retriesExhausted')}</Alert>
        ) : (
          <>
            {/* ---- CONSENT ---- */}
            {phase === 'consent' && (
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Stack spacing={1} alignItems="center" sx={{ mb: 2, textAlign: 'center' }}>
                    <FaceRetouchingNaturalIcon color="primary" sx={{ fontSize: 48 }} />
                    <Typography variant="body2" color="text.secondary">
                      {isConsultation
                        ? t('faceCapture.subtitleConsultation')
                        : t('faceCapture.subtitle', {
                            clinic: policy.clinicName || clinicName || clinicCode,
                          })}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
                    <Chip size="small" label={t('faceCapture.attemptsLeft', { n: policy.retriesLeft })} />
                    <Chip size="small" variant="outlined" label={t('faceCapture.validity', { h: policy.photoValidityHours })} />
                  </Stack>
                  {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                  <FormControlLabel
                    control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
                    label={<Typography variant="body2">{isConsultation ? t('faceCapture.consentConsultation') : t('faceCapture.consent')}</Typography>}
                  />
                  <Button fullWidth variant="contained" size="large" sx={{ mt: 2 }} disabled={!consent}
                    onClick={() => setPhase((!inviteId && policy.agentChoice === 'client') ? 'agent' : 'capture')}>
                    {t('faceCapture.start')}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* ---- AGENT (only if the clinic lets the client choose) ---- */}
            {phase === 'agent' && (
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Stack spacing={1} alignItems="center" sx={{ mb: 2, textAlign: 'center' }}>
                    <SmartToyIcon color="primary" sx={{ fontSize: 40 }} />
                    <Typography variant="body2" color="text.secondary">{t('faceCapture.pickAgent')}</Typography>
                  </Stack>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('faceCapture.agent')}</InputLabel>
                    <Select label={t('faceCapture.agent')} value={agentKey} onChange={(e) => setAgentKey(e.target.value)}>
                      {(policy.agents || []).map((a) => (
                        <MenuItem key={a.key} value={a.key}>{localized(a.name, lang)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button fullWidth variant="contained" size="large" sx={{ mt: 2 }} disabled={!agentKey}
                    onClick={() => setPhase('capture')}>
                    {t('faceCapture.continue')}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* ---- CAPTURE (identical flow to the clinic app) ---- */}
            {phase === 'capture' && (
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <FaceCaptureFlow onComplete={(result) => { setCaptures(result); setPhase('review'); }} />
                </CardContent>
              </Card>
            )}

            {/* ---- REVIEW ---- */}
            {phase === 'review' && (
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('faceCapture.reviewHelp', { n: captures.length })}
                  </Typography>
                  {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
                    {captures.map((c, i) => (
                      <Box key={i} component="img" src={c.image} alt=""
                        sx={{ width: '100%', borderRadius: 2, aspectRatio: '3/4', objectFit: 'cover' }} />
                    ))}
                  </Box>
                  {submitMutation.isPending && (
                    <LinearProgress variant="determinate" sx={{ mb: 2 }}
                      value={captures.length ? (uploadDone / captures.length) * 100 : 0} />
                  )}
                  <Stack direction="row" spacing={1}>
                    <Button fullWidth variant="outlined" disabled={submitMutation.isPending}
                      onClick={() => { setCaptures([]); setError(''); setPhase('capture'); }}>
                      {t('faceCapture.retake')}
                    </Button>
                    <Button fullWidth variant="contained" disabled={submitMutation.isPending}
                      startIcon={submitMutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
                      onClick={() => submitMutation.mutate()}>
                      {submitMutation.isPending
                        ? t('faceCapture.sending', { done: uploadDone, total: captures.length })
                        : t('faceCapture.submit')}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* ---- SUCCESS ---- */}
            {phase === 'success' && (
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', py: 2 }}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
                    <Typography variant="h6" fontWeight={800}>{t('faceCapture.doneTitle')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isConsultation
                        ? t('faceCapture.doneConsultation')
                        : t('faceCapture.doneBody', {
                            clinic: policy.clinicName || clinicName || clinicCode,
                          })}
                    </Typography>
                    {/* A consultation capture goes straight to the free
                        preview — that is the payoff for having captured, and
                        it is what the whole free stage exists to show. */}
                    {isConsultation ? (
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate(
                          consultationId
                            ? `/consultations/${encodeURIComponent(consultationId)}/preview`
                            : '/consultations',
                        )}
                      >
                        {t('faceCapture.seePreview')}
                      </Button>
                    ) : (
                      <Button variant="contained" onClick={() => navigate('/dashboard')}>
                        {t('faceCapture.backToDashboard')}
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </>
        )}
    </Container>
  );
}
